/* External */
import {
  call, put, takeEvery, takeLatest, throttle, delay,
} from 'redux-saga/effects';
import { isCollection, OrderedSet, Set } from 'immutable';
import { addDays } from 'date-fns';

/* Ott-common */
import { PROVIDER } from '@dtvgo/backend-service/normalize';

/* ActionTypes */
import {
  FETCH_ALL_CHANNELS,
  FETCH_CHANNEL_SCHEDULES,
  SCHEDULE_FETCH_CHANNEL_SCHEDULES,
  FETCH_CHANNEL_SCHEDULES_BY_EVENT_TYPE,
} from './actionTypes';

import { addToEntities } from '../utils/actions';
import { MAX_FETCH_ATTEMPTS, RETRY_DELAY_MS, schedulesMaxChannels } from '../../utils/env';
import { setIsFetchingChannels } from '../status';
import {
  requestCreate, requestFailureMultiple, requestSuccessMultiple,
} from '../requests/actions';

/* Sagas */
import { requestSaga } from '../requests/sagas';
import { authenticatedSaga } from '../utils/sagas';

/* Services */
import backendService from '../../services/backendService';

// Safe fallback used when the env variable is missing, zero, or non-numeric.
// 5 is intentionally conservative for Hisense Legacy / old Tizen / old WebOS:
// smaller payloads → lower memory pressure → more stable parsing on weak CPUs.
// Increase via VITE_EPG_SCHEDULES_MAX_CHANNELS for stronger TV platforms.
const SCHEDULES_BATCH_SIZE_FALLBACK = 5;
const MAX_CHANNEL_IN_SCHEDULES_REQUEST = (
  Number.isFinite(schedulesMaxChannels) && schedulesMaxChannels > 0
    ? schedulesMaxChannels
    : SCHEDULES_BATCH_SIZE_FALLBACK
);
const TIME_TO_WAIT_TO_FETCH_SCHEDULES_MS = 1000;

export default [
  fetchAllChannels,
  fetchChannelSchedules(PROVIDER.VRIO),
  scheduleFetchChannelSchedules,
  fetchChannelSchedulesByEventType,
];

function* fetchAllChannels() {
  yield takeLatest(FETCH_ALL_CHANNELS, authenticatedSaga(fetchAllChannelsSaga));
}

function fetchChannelSchedules(provider) {
  return function* fetchChannelSchedulesByProvider() {
    yield throttle(
      TIME_TO_WAIT_TO_FETCH_SCHEDULES_MS,
      FETCH_CHANNEL_SCHEDULES[provider],
      authenticatedSaga(fetchChannelSchedulesSaga(provider)),
    );
  };
}

function* scheduleFetchChannelSchedules() {
  yield takeEvery(
    SCHEDULE_FETCH_CHANNEL_SCHEDULES,
    authenticatedSaga(scheduleFetchChannelSchedulesSaga),
  );
}

function* fetchChannelSchedulesByEventType() {
  yield takeEvery(
    FETCH_CHANNEL_SCHEDULES_BY_EVENT_TYPE,
    authenticatedSaga(fetchChannelSchedulesByEventTypeSaga),
  );
}

function* fetchAllChannelsSaga(action) {
  yield put(setIsFetchingChannels(true));
  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt += 1) {
    try {
      const response = yield call(requestSaga, fetchChannelsService, action);
      if (response) {
        const { normalizedData: { entities } } = response;
        yield put(addToEntities(entities));
        break;
      }
    } catch (error) {
      console.error('channels>sagas>fetchAllChannelsSaga', error);
    }
    if (attempt === MAX_FETCH_ATTEMPTS) {
      console.error('channels>sagas>fetchAllChannelsSaga - max retries reached');
    } else {
      yield delay(RETRY_DELAY_MS);
    }
  }
  yield put(setIsFetchingChannels(false));
}

function fetchChannelsService(assetToken = false) {
  return backendService.content.v2.getChannels(assetToken);
}

async function fetchChannelSchedulesService({
  channelId, schedulesForChannel, sourceProvider, ...actionPayload
}) {
  return backendService.content.v2.getSchedules({
    channelId,
    pageNumber: 0,
    pageSize: schedulesForChannel * (channelId?.size || 0),
    assetToken: false,
    ...actionPayload,
  });
}

const DEFAULT_SCHEDULES_FOR_CHANNEL = 1;

function createDataToFetchSchedules(sourceProvider) {
  return {
    sourceProvider,
    channelIds: OrderedSet(),
    schedulesForChannel: DEFAULT_SCHEDULES_FOR_CHANNEL,
    fromDatetime: undefined,
    toDatetime: undefined,
    requestIds: Set(),
    actionType: FETCH_CHANNEL_SCHEDULES[sourceProvider],
  };
}

const providersToFetchSchedules = {
  [PROVIDER.VRIO]: createDataToFetchSchedules(PROVIDER.VRIO),
};

function fetchChannelSchedulesSaga(sourceProvider) {
  const dataToFetchSchedules = providersToFetchSchedules[sourceProvider];

  return function* createdFetchChannelSchedulesSaga() {
    const {
      channelIds,
      fromDatetime = new Date(),
      toDatetime = addDays(new Date(), 1),
      schedulesForChannel,
      requestIds,
    } = dataToFetchSchedules;
    fromDatetime.setSeconds(0, 0);
    toDatetime.setSeconds(0, 0);

    dataToFetchSchedules.requestIds = requestIds.clear();
    dataToFetchSchedules.channelIds = channelIds.clear();
    dataToFetchSchedules.schedulesForChannel = DEFAULT_SCHEDULES_FOR_CHANNEL;
    dataToFetchSchedules.fromDatetime = undefined;
    dataToFetchSchedules.toDatetime = undefined;

    try {
      const groupedChannelIds = channelIds.toList().groupBy(
        (stationId, index) => Math.floor(index / MAX_CHANNEL_IN_SCHEDULES_REQUEST),
      );

      for (const [, newChannelIds] of groupedChannelIds) {
        for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt += 1) {
          try {
            const response = yield call(fetchChannelSchedulesService, {
              channelId: newChannelIds,
              startTime: fromDatetime,
              endTime: toDatetime,
              schedulesForChannel,
              sourceProvider,
            });
            if (response) {
              const { normalizedData: { entities } } = response;
              yield put(addToEntities(entities));
            }
            break;
          } catch (error) {
            if (attempt === MAX_FETCH_ATTEMPTS) {
              console.error('stations>fetchChannelSchedulesSaga>group', error);
              throw error;
            } else {
              yield delay(RETRY_DELAY_MS);
            }
          }
        }
      }
      yield put(requestSuccessMultiple(requestIds));
    } catch (error) {
      console.error('stations>fetchChannelSchedulesSaga', error);
      yield put(requestFailureMultiple(requestIds));
    }
  };
}

function concatOrAdd(collection, elementOrElements) {
  let newCollection = collection;
  newCollection = isCollection(elementOrElements)
    /* eslint-disable-next-line unicorn/prefer-spread
        -- newCollection is not an array, is an immutable Collection */
    ? newCollection.concat(elementOrElements)
    : newCollection.add(elementOrElements);
  return newCollection;
}

function* scheduleFetchChannelSchedulesSaga(action) {
  const {
    requestId,
    payload: {
      channelIdOrIds,
      sourceProvider,
      schedulesForChannel: newSchedulesForChannel,
      fromDatetime: newFromDatetime,
      toDatetime: newToDatetime,
    },
  } = action;

  if (requestId) {
    yield put(requestCreate({ requestId }));
  }

  const dataToFetchSchedules = providersToFetchSchedules[sourceProvider || PROVIDER.VRIO];
  if (dataToFetchSchedules) {
    const {
      channelIds,
      schedulesForChannel,
      fromDatetime,
      toDatetime,
      requestIds,
      actionType,
    } = dataToFetchSchedules;

    dataToFetchSchedules.channelIds = concatOrAdd(channelIds, channelIdOrIds);
    if (requestId) dataToFetchSchedules.requestIds = concatOrAdd(requestIds, requestId);
    dataToFetchSchedules.schedulesForChannel = Math.max(
      schedulesForChannel,
      newSchedulesForChannel,
    );
    if (!fromDatetime || !newFromDatetime || newFromDatetime < fromDatetime) {
      dataToFetchSchedules.fromDatetime = newFromDatetime;
    }
    if (!toDatetime || !newToDatetime || newToDatetime > toDatetime) {
      dataToFetchSchedules.toDatetime = newToDatetime;
    }

    yield put({ type: actionType });
  }
}

async function fetchChannelSchedulesByEventTypeService({
  channelId,
  eventType,
  pageSize,
}) {
  return backendService.content.v2.getSchedulesByEventType(channelId, {
    eventType,
    pageSize,
    assetToken: false,
  });
}

function* fetchChannelSchedulesByEventTypeSaga(action) {
  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt += 1) {
    try {
      const response = yield call(requestSaga, fetchChannelSchedulesByEventTypeService, action);
      if (response) {
        const { normalizedData: { entities } } = response;
        yield put(addToEntities(entities));
      }
      break;
    } catch (error) {
      if (attempt === MAX_FETCH_ATTEMPTS) {
        console.error('channels>sagas>fetchChannelSchedulesByEventTypeSaga', error);
      } else {
        yield delay(RETRY_DELAY_MS);
      }
    }
  }
}
